import React, { useEffect, useState } from 'react';
import { Auth, Hub } from 'aws-amplify';
import {
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import {
  Button,
  Checkbox,
  Form,
  Grid,
  Image,
  Message,
  Segment,
} from 'semantic-ui-react';
import IdleTimer from 'react-idle-timer';
import logo from '../../logo.png';
import authStorage from '../../AuthenticationStorage';
import styles from './Authenticator.style.module.css';

type View =
  | 'loading'
  | 'enterEmail'
  | 'signUp'
  | 'linkSent'
  | 'verifying'
  | 'signedIn';

interface PendingMagicLink {
  email: string;
  session: string;
  ts: number;
}

interface Props {
  children?: React.ReactNode;
}

const PENDING_KEY = 'magic-link-pending';
const PENDING_TTL_MS = 15 * 60 * 1000;

const readPending = (): PendingMagicLink | null => {
  const raw = localStorage.getItem(PENDING_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as PendingMagicLink;
    if (!parsed.email || !parsed.session || !parsed.ts) return null;
    if (Date.now() - parsed.ts > PENDING_TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
};

const clearPending = () => localStorage.removeItem(PENDING_KEY);

const writePending = (email: string, session: string) => {
  const value: PendingMagicLink = { email, session, ts: Date.now() };
  localStorage.setItem(PENDING_KEY, JSON.stringify(value));
};

// Reconstructs a CognitoUser bound to our existing AuthenticationStorage so the
// tokens issued by sendCustomChallengeAnswer land in the same place amplify
// reads from — which is what lets Auth.currentAuthenticatedUser() succeed right
// after redemption without further plumbing.
const redeemMagicLink = (
  email: string,
  session: string,
  token: string
): Promise<CognitoUserSession> => {
  return new Promise((resolve, reject) => {
    const pool = new CognitoUserPool({
      UserPoolId: process.env.REACT_APP_USERPOOL_ID as string,
      ClientId: process.env.REACT_APP_USERPOOL_CLIENT_ID as string,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      Storage: authStorage as any,
    });
    const user = new CognitoUser({
      Username: email,
      Pool: pool,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      Storage: authStorage as any,
    });
    // Session and challengeName aren't on the public type but are required for
    // sendCustomChallengeAnswer to know where to continue from.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (user as any).Session = session;
    user.sendCustomChallengeAnswer(token, {
      onSuccess: (s) => resolve(s),
      onFailure: (err) => reject(err),
    });
  });
};

const AgeReadyAuthenticator: React.FC<Props> = ({ children }) => {
  const [view, setView] = useState<View>('loading');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [rememberMe, setRememberMe] = useState(
    localStorage.getItem('remember-me') === 'true'
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await Auth.currentAuthenticatedUser();
        if (!cancelled) setView('signedIn');
        return;
      } catch {
        if (cancelled) return;
      }

      const url = new URL(window.location.href);
      const token = url.searchParams.get('token');
      if (!token) {
        setView('enterEmail');
        return;
      }

      const pending = readPending();
      if (!pending) {
        window.history.replaceState({}, '', '/');
        setError(
          'It looks like the sign-in link was opened in a different browser ' +
          'than the one where you requested it. Enter your email below to ' +
          'get a new link.'
        );
        setView('enterEmail');
        return;
      }

      setEmail(pending.email);
      setView('verifying');
      try {
        await redeemMagicLink(pending.email, pending.session, token);
        await Auth.currentAuthenticatedUser();
        if (cancelled) return;
        clearPending();
        window.history.replaceState({}, '', '/');
        setView('signedIn');
      } catch (err) {
        if (cancelled) return;
        clearPending();
        window.history.replaceState({}, '', '/');
        const message =
          err instanceof Error
            ? err.message
            : 'This sign-in link is invalid or has expired.';
        setError(message);
        setView('enterEmail');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const listener = (data: { payload?: { event?: string } }) => {
      if (data?.payload?.event === 'signOut') {
        clearPending();
        setEmail('');
        setError(null);
        setView('enterEmail');
        window.history.replaceState({}, '', '/');
      }
    };
    Hub.listen('auth', listener);
    return () => {
      Hub.remove('auth', listener);
    };
  }, []);

  const onIdle = () => {
    if (!rememberMe) {
      Auth.signOut();
    }
  };

  const onRememberMeChange = (_: unknown, data: { checked?: boolean }) => {
    const checked = !!data.checked;
    setRememberMe(checked);
    authStorage.setRememberMe(checked);
    localStorage.setItem('remember-me', String(checked));
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const normalizedEmail = email.trim().toLowerCase();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const user = (await Auth.signIn(normalizedEmail)) as any;
      writePending(normalizedEmail, user.Session);
      setView('linkSent');
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Sign-in failed. Please try again.';
      setError(message);
    } finally {
      setBusy(false);
    }
  };

  const handleSignUpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const normalizedEmail = email.trim().toLowerCase();
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/signup`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: normalizedEmail,
            firstName: firstName.trim(),
            lastName: lastName.trim(),
          }),
        }
      );
      if (!response.ok) {
        const body = await response.text();
        throw new Error(body || 'Sign-up failed. Please try again.');
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const user = (await Auth.signIn(normalizedEmail)) as any;
      writePending(normalizedEmail, user.Session);
      setView('linkSent');
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Sign-up failed. Please try again.';
      setError(message);
    } finally {
      setBusy(false);
    }
  };

  const resetToEmail = () => {
    setError(null);
    clearPending();
    setView('enterEmail');
  };

  const goToSignUp = () => {
    setError(null);
    setFirstName('');
    setLastName('');
    setView('signUp');
  };

  if (view === 'signedIn') {
    return (
      <div
        style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}
      >
        <IdleTimer onIdle={onIdle} timeout={1000 * 60 * 30} startOnMount />
        {children}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', padding: '15px' }}>
      <div
        style={{
          backgroundColor: 'rgba(255,255,255)',
          maxWidth: '400px',
          width: '100%',
          borderRadius: 15,
          margin: 'auto',
        }}
      >
        <Grid padded columns={1} className={styles['login-and-register']}>
          <Grid.Column textAlign="center">
            <Segment basic className={styles['main-logo']}>
              <Image
                size="medium"
                src={logo}
                alt="age-ready-logo"
                style={{
                  maxWidth: '300px',
                  width: '100%',
                  margin: 'auto',
                  display: 'flex',
                }}
              />
            </Segment>

            {view === 'loading' && <Message info content="Loading…" />}

            {view === 'enterEmail' && (
              <Form onSubmit={handleEmailSubmit}>
                <Form.Input
                  data-testid="email-input"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  required
                  onChange={(_, d) => setEmail(d.value)}
                  autoFocus
                />
                <Form.Field>
                  <Checkbox
                    data-testid="remember me"
                    checked={rememberMe}
                    onChange={onRememberMeChange}
                    label="Remember me"
                  />
                </Form.Field>
                {error && <Message negative content={error} />}
                <Button
                  primary
                  type="submit"
                  loading={busy}
                  disabled={busy}
                  fluid
                >
                  Email me a sign-in link
                </Button>
                <div style={{ marginTop: 12, fontSize: 13, color: '#828282' }}>
                  Don&apos;t have an account?{' '}
                  <a
                    data-testid="create-account-link"
                    href="#create-account"
                    onClick={(e) => {
                      e.preventDefault();
                      goToSignUp();
                    }}
                    style={{
                      color: 'rgb(0, 52, 104)',
                      textDecoration: 'underline',
                    }}
                  >
                    Create one
                  </a>
                </div>
              </Form>
            )}

            {view === 'signUp' && (
              <Form onSubmit={handleSignUpSubmit}>
                <Form.Input
                  data-testid="signup-first-name"
                  placeholder="First name"
                  value={firstName}
                  required
                  onChange={(_, d) => setFirstName(d.value)}
                  autoFocus
                />
                <Form.Input
                  data-testid="signup-last-name"
                  placeholder="Last name"
                  value={lastName}
                  required
                  onChange={(_, d) => setLastName(d.value)}
                />
                <Form.Input
                  data-testid="signup-email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  required
                  onChange={(_, d) => setEmail(d.value)}
                />
                {error && <Message negative content={error} />}
                <Button
                  primary
                  type="submit"
                  loading={busy}
                  disabled={busy}
                  fluid
                >
                  Create account
                </Button>
                <Button
                  basic
                  type="button"
                  onClick={resetToEmail}
                  fluid
                  style={{ marginTop: 8 }}
                >
                  Already have an account? Sign in
                </Button>
              </Form>
            )}

            {view === 'linkSent' && (
              <div data-testid="link-sent">
                <Message info>
                  We sent a sign-in link to <strong>{email}</strong>. Open the
                  email and click the link to
                  sign in.
                </Message>
                {error && <Message negative content={error} />}
                <Button
                  basic
                  type="button"
                  onClick={resetToEmail}
                  fluid
                  style={{ marginTop: 8 }}
                >
                  Use a different email
                </Button>
              </div>
            )}

            {view === 'verifying' && (
              <Message info content="Signing you in…" />
            )}

            <a
              style={{
                color: 'rgb(0, 52, 104)',
                textDecoration: 'underline',
                display: 'block',
                marginTop: 16,
              }}
              href="https://beageready.com/terms-of-use/"
              target="_blank"
              rel="noreferrer"
            >
              Terms of Use
            </a>
          </Grid.Column>
        </Grid>
      </div>
    </div>
  );
};

export default AgeReadyAuthenticator;
